import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { X, HeartHandshake, BellRing } from 'lucide-react';
import { churchInfo } from '../data/mock';

const WhatsNewPopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Bump this when there's something new to announce.
  const CURRENT_VERSION = '2.3.0';

  const updates = [
    {
      Icon: HeartHandshake,
      title: 'Inspirational Posts',
      description: 'Share, uplift, and encourage.',
    },
    {
      Icon: BellRing,
      title: 'Stay Connected',
      description:
        'Turn on notifications to never miss a message, prayer request, or birthday celebration.',
    },
  ];

  useEffect(() => {
    // Show only once per version, ever.
    const seenVersion = localStorage.getItem('whatsNewVersion');
    if (seenVersion !== CURRENT_VERSION) {
      const timer = setTimeout(() => setIsOpen(true), 1800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem('whatsNewVersion', CURRENT_VERSION);
    setIsOpen(false);
  };

  // Safety: restore interactivity after close (Radix can leave pointer-events:none on body)
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        document.body.style.pointerEvents = '';
      }, 150);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent
        data-testid="whats-new-dialog"
        className="max-w-sm w-[calc(100%-1.5rem)] max-h-[88vh] flex flex-col mx-auto rounded-3xl p-0 gap-0 overflow-hidden border-0 shadow-2xl"
      >
        <DialogTitle className="sr-only">What's New</DialogTitle>
        <DialogDescription className="sr-only">Latest updates in the app</DialogDescription>

        {/* Header */}
        <div className="relative bg-gradient-to-br from-navy-900 to-navy-700 px-6 pt-8 pb-9 text-center flex-shrink-0">
          <button
            onClick={handleClose}
            aria-label="Close"
            data-testid="whats-new-close-btn"
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full bg-white/10 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="mx-auto mb-3 h-16 w-16 flex items-center justify-center rounded-2xl bg-white ring-1 ring-tan-400/30 overflow-hidden">
            <img
              src={churchInfo.logo}
              alt="Second Missionary Baptist Church"
              className="h-14 w-14 object-contain"
            />
          </div>
          <p className="text-tan-400 text-xs font-semibold tracking-[0.2em] uppercase mb-1">Just Updated</p>
          <h2 className="text-2xl font-bold text-white">What's New</h2>
        </div>

        {/* Updates */}
        <div className="px-6 py-6 overflow-y-auto flex-1 bg-white space-y-5">
          {updates.map(({ Icon, title, description }, index) => (
            <div key={index} className="flex items-start gap-4" data-testid={`whats-new-item-${index}`}>
              <div className="flex-shrink-0 h-11 w-11 flex items-center justify-center rounded-xl bg-navy-50 ring-1 ring-navy-100">
                <Icon className="w-5 h-5 text-navy-700" strokeWidth={1.75} />
              </div>
              <div className="pt-0.5">
                <h3 className="text-base font-bold text-navy-900 leading-tight mb-1">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 flex-shrink-0 bg-white">
          <Button
            onClick={handleClose}
            data-testid="whats-new-got-it-btn"
            className="w-full bg-navy-900 hover:bg-navy-800 text-white text-base py-6 rounded-2xl font-semibold shadow-md active:scale-[0.98] transition-transform"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewPopup;
